import React, { useState } from 'react'
import { Permission, Research, User } from '../../services/models'
import ResearchFileListComponent from '../ResearchFileListComponent/ResearchFileListComponent'
import { Avatar, Drawer, Image, List, Tag, Typography } from 'antd'
import { API_URL } from '../../services/apiService'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useAppSelector } from '../../app/hooks'
import { permItems } from '../../features/perm/permSlice'

dayjs.extend(localizedFormat)

// dayjs(undefined) подставляет текущую дату, поэтому отсутствие updatedAt
// обязательно обрабатывается явно — иначе вышел бы ложный «обновлено: сегодня».
const isEdited = (item: Research) => !!item.updatedAt && dayjs(item.updatedAt).isAfter(dayjs(item.createdAt))

// Разговорный формат «6 октября 2026» вместо 06/10/2026: список читают
// глазами, а не сравнивают цифры.
const formatDate = (value: string) => dayjs(value).locale('ru').format('D MMMM YYYY')

const META_FONT_SIZE = 13
const TITLE_FONT_SIZE = 16

type Props = {
    items: Research[]
}
const ResearchListComponent: React.FC<Props> = ({ items }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState<User>()
  const permissions = useAppSelector(permItems)
  const permissionByServiceId = new Map<number, Permission>()
  permissions.forEach((p) => {
    permissionByServiceId.set(p.serviceId, p)
  })

  const renderTag = (item: Research) => {
    if (!permissionByServiceId.has(item.service.id)) {
      return <Tag color="red">закрыто</Tag>
    }
    if (!item.view) {
      return <Tag color="cyan">новое</Tag>
    }
    return <></>
  }
  // Заголовок поднимаем явно: .ant-list-item-meta-title задаёт fontSize,
  // равный обычному тексту, поэтому иерархии размеров у карточки нет.
  const renderTitle = (item: Research) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <a
                onClick={() => navigate(`/analytics/${item.service.id}/research/${item.id}`)}
                style={{ fontSize: TITLE_FONT_SIZE, fontWeight: 600, lineHeight: 1.4 }}
            >
                {item.title}
            </a>
            {renderTag(item)}
        </div>
  )
  // Метаданные одной строкой: автор и дата в одном ритме, разделитель «·».
  // Дата обновления показывается только если материал действительно правили:
  // у неизменённых исследований updatedAt совпадает с createdAt, и вторая
  // одинаковая дата в списке выглядела бы как ошибка, а не как информация.
  const renderMeta = (item: Research) => (
        <div style={{ fontSize: META_FONT_SIZE, color: 'rgba(0, 0, 0, 0.45)', marginTop: 4 }}>
            <span onClick={() => setUser(item.user)} style={{ cursor: 'pointer' }}>
                {item.user.firstName} {item.user.lastName}
            </span>
            <span> · {formatDate(item.createdAt)}</span>
            {isEdited(item) && <span> · обновлено {formatDate(item.updatedAt)}</span>}
        </div>
  )

  const renderDescription = (item: Research) => (
        <div>
            {renderMeta(item)}
            <div style={{ marginTop: 4 }}>
                <ResearchFileListComponent files={item.files}/>
            </div>
        </div>
  )
  const renderUser = (user: User) => (
        <>
            <Typography.Title level={4} style={{ margin: 0 }}>{user.firstName} {user.lastName}</Typography.Title>
            <Typography.Title level={5} style={{ margin: 0 }}>{user.job}</Typography.Title>
            <div style={{ padding: '10px' }}>
                <Image width={100} src={`${API_URL}/avatar/${user.id}`}/>
            </div>
            <Typography.Paragraph>{user.biography}</Typography.Paragraph>
            {user.contactEmail && (
              <div style={{ marginTop: 8 }}>
                <Typography.Text strong>email: </Typography.Text>
                <Typography.Text copyable>{user.contactEmail}</Typography.Text>
              </div>
            )}
        </>
  )
  return (
        <>
            <List
                dataSource={items}
                renderItem={item => (
                    <List.Item key={item.id}>
                        <List.Item.Meta
                            avatar={<Avatar size={40} src={`${API_URL}/avatar/${item.user.id}`}/>}
                            title={renderTitle(item)}
                            description={renderDescription(item)}
                        />
                    </List.Item>
                )}
            />
            <Drawer title="Автор" placement="right" onClose={() => setUser(undefined)}
                    open={user !== undefined}>
                {user && renderUser(user)}
            </Drawer>
        </>
  )
}

export default ResearchListComponent
